import React, { useState } from 'react';
import { Briefcase, Users, ArrowRight, Check } from 'lucide-react';
import { Input, Button } from './UIComponents';

interface OnboardingProps {
  onComplete: (role: 'PROVIDER' | 'RECEIVER', workspaceName?: string) => Promise<void>;
}

export const Onboarding: React.FC<OnboardingProps> = ({ onComplete }) => {
  const [selectedRole, setSelectedRole] = useState<'PROVIDER' | 'RECEIVER' | null>(null);
  const [workspaceName, setWorkspaceName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const placeholder =
    selectedRole === 'RECEIVER'
      ? 'Receiver company or team name'
      : 'Provider / studio / personal brand';

  const suggestedFallback = 'My Workspace';

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      if ('stopPropagation' in e) {
        e.stopPropagation();
      }
    }

    // Double-check role is selected
    if (!selectedRole) {
      setError('Please select a role before continuing.');
      return;
    }

    // Prevent double submission
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Pass empty string if no name provided - parent will generate unique name
      const name = workspaceName.trim() || '';
      await onComplete(selectedRole, name);
      // onComplete will handle navigation, so we don't need to do anything here
    } catch (err: any) {
      // If onComplete throws, show error
      setError(err.message || 'Failed to create workspace. Please try again.');
      console.error('Onboarding error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 animate-fade-in">
      {/* Logo */}
      <div className="absolute top-8 left-8 flex items-center gap-2.5 opacity-60">
        <div className="w-7 h-7 bg-zinc-900 rounded-lg flex items-center justify-center border border-zinc-800">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-zinc-400">
            <path d="M5 5L12 19L19 5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <span className="font-medium text-sm tracking-tight text-zinc-300">Verline</span>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-4xl mx-auto mt-16 mb-32">
        <div className="text-center mb-12">
          <h1 className="text-2xl md:text-3xl font-medium text-white mb-3 tracking-tight">
            Choose your role
          </h1>
          <p className="text-zinc-500 text-sm">
            Select the option that best describes how you'll use Verline
          </p>
        </div>

        {/* Role Cards */}
        <div className="grid md:grid-cols-2 gap-4 mb-12">
          {/* Provider Card */}
          <button
            type="button"
            role="radio"
            aria-checked={selectedRole === 'PROVIDER'}
            aria-label="Select Provider role"
            tabIndex={0}
            onClick={() => {
              setSelectedRole('PROVIDER');
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSelectedRole('PROVIDER');
                setError(null);
              }
            }}
            className={`group relative p-6 md:p-8 rounded-xl border-2 text-left transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:ring-offset-2 focus:ring-offset-black ${selectedRole === 'PROVIDER'
              ? 'border-indigo-500/50 bg-indigo-500/5'
              : selectedRole !== null
                ? 'border-zinc-800 bg-zinc-950/30 opacity-60'
                : 'border-zinc-800 bg-zinc-950/50 hover:border-zinc-700 hover:bg-zinc-950'
              }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-200 ${selectedRole === 'PROVIDER'
                ? 'bg-indigo-500/20 text-indigo-400'
                : 'bg-zinc-900 text-zinc-500'
                }`}>
                <Briefcase size={20} strokeWidth={1.5} />
              </div>
              {selectedRole === 'PROVIDER' && (
                <div className="w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center flex-shrink-0">
                  <Check size={14} className="text-white" strokeWidth={2.5} />
                </div>
              )}
            </div>

            <h2 className="text-lg font-medium text-white mb-2 tracking-tight">
              I manage work
            </h2>
            <p className={`text-sm leading-relaxed transition-colors duration-200 ${selectedRole === 'PROVIDER' ? 'text-zinc-300' : 'text-zinc-500'
              }`}>
              For freelancers, retainers, or providers who manage receiver projects.
            </p>
          </button>

          {/* Receiver Card */}
          <button
            type="button"
            role="radio"
            aria-checked={selectedRole === 'RECEIVER'}
            aria-label="Select Receiver role"
            tabIndex={0}
            onClick={() => {
              setSelectedRole('RECEIVER');
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSelectedRole('RECEIVER');
                setError(null);
              }
            }}
            className={`group relative p-6 md:p-8 rounded-xl border-2 text-left transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:ring-offset-2 focus:ring-offset-black ${selectedRole === 'RECEIVER'
              ? 'border-emerald-500/50 bg-emerald-500/5'
              : selectedRole !== null
                ? 'border-zinc-800 bg-zinc-950/30 opacity-60'
                : 'border-zinc-800 bg-zinc-950/50 hover:border-zinc-700 hover:bg-zinc-950'
              }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-200 ${selectedRole === 'RECEIVER'
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'bg-zinc-900 text-zinc-500'
                }`}>
                <Users size={20} strokeWidth={1.5} />
              </div>
              {selectedRole === 'RECEIVER' && (
                <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <Check size={14} className="text-white" strokeWidth={2.5} />
                </div>
              )}
            </div>

            <h2 className="text-lg font-medium text-white mb-2 tracking-tight">
              I work with a provider
            </h2>
            <p className={`text-sm leading-relaxed transition-colors duration-200 ${selectedRole === 'RECEIVER' ? 'text-zinc-300' : 'text-zinc-500'
              }`}>
              For receivers and teams collaborating with providers, freelancers, or retainers.
            </p>
          </button>
        </div>

        {/* Workspace Name Input */}
        {selectedRole && (
          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto animate-fade-in">
            <div className="mb-6">
              <label className="text-xs uppercase tracking-wider text-zinc-500 font-medium mb-2 block">
                Workplace name (Optional)
              </label>
              <Input
                placeholder={placeholder}
                value={workspaceName}
                onChange={(e) => {
                  setWorkspaceName(e.target.value);
                  setError(null);
                }}
                className="bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-zinc-700"
                autoFocus
                disabled={isSubmitting}
              />
              <p className="text-xs text-zinc-600 mt-2">
                Default: "My Workspace"
              </p>
            </div>

            {error && (
              <div className="mb-4 text-red-400 text-sm bg-red-950/20 border border-red-900/30 p-3 rounded">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full md:w-auto md:min-w-[200px]"
              size="lg"
              disabled={!selectedRole || isSubmitting}
              isLoading={isSubmitting}
              aria-label="Continue to workspace"
            >
              Continue <ArrowRight size={16} className="ml-2" />
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};